import { useState, useEffect, useRef } from 'react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Checkbox } from '../components/ui/checkbox';
import { toast } from 'sonner';
import { Calendar, Users, Building2, CheckCircle2, Shield, Clock, Headphones, Sparkles, Rocket, ChevronDown, X, MapPin, Package } from 'lucide-react';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// US States list (50 states + DC + Puerto Rico = 52)
const US_STATES = [
  'Alabama', 'Alaska', 'Arizona', 'Arkansas', 'California', 'Colorado', 'Connecticut', 
  'Delaware', 'District of Columbia', 'Florida', 'Georgia', 'Hawaii', 'Idaho', 'Illinois', 
  'Indiana', 'Iowa', 'Kansas', 'Kentucky', 'Louisiana', 'Maine', 'Maryland', 'Massachusetts', 
  'Michigan', 'Minnesota', 'Mississippi', 'Missouri', 'Montana', 'Nebraska', 'Nevada', 
  'New Hampshire', 'New Jersey', 'New Mexico', 'New York', 'North Carolina', 'North Dakota', 
  'Ohio', 'Oklahoma', 'Oregon', 'Pennsylvania', 'Puerto Rico', 'Rhode Island', 'South Carolina', 
  'South Dakota', 'Tennessee', 'Texas', 'Utah', 'Vermont', 'Virginia', 'Washington', 
  'West Virginia', 'Wisconsin', 'Wyoming'
];

// DME Categories (17 Product Categories)
const DME_CATEGORIES = [
  'Emergency / Monitoring',
  'Orthopedic / Orthotics',
  'Mobility Equipment',
  'Hospital Beds & Accessories',
  'Bathroom Safety',
  'Diabetic Supplies',
  'Wound Care',
  'Prosthetics',
  'Speech & Communication',
  'Enteral Nutrition',
  'Hearing Aids',
  'CPAP/BiPAP Accessories',
  'Urology / Ostomy',
  'Pediatric DME',
  'Bariatric Equipment',
  'Pain Management / Therapeutic'
];

export const DemoPage = () => {
  const [formData, setFormData] = useState({
    firstName: '', lastName: '', email: '', phone: '', company: '', jobTitle: '',
    companySize: '', currentSolution: '', interests: [], message: '', subscribeNewsletter: false,
    licensedStates: [], dmeCategories: [],
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [statesDropdownOpen, setStatesDropdownOpen] = useState(false);
  const [categoriesDropdownOpen, setCategoriesDropdownOpen] = useState(false);
  const statesRef = useRef(null);
  const categoriesRef = useRef(null);

  // Close dropdowns when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (statesRef.current && !statesRef.current.contains(event.target)) {
        setStatesDropdownOpen(false);
      }
      if (categoriesRef.current && !categoriesRef.current.contains(event.target)) {
        setCategoriesDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSelectChange = (name, value) => {
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleMultiSelectChange = (field, value) => {
    setFormData((prev) => ({
      ...prev,
      [field]: prev[field].includes(value)
        ? prev[field].filter((item) => item !== value)
        : [...prev[field], value],
    }));
  };

  const handleInterestChange = (interest) => {
    setFormData((prev) => ({
      ...prev,
      interests: prev.interests.includes(interest)
        ? prev.interests.filter((i) => i !== interest)
        : [...prev.interests, interest],
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    try {
      await axios.post(`${API_URL}/api/demo-request`, { ...formData, fullName: `${formData.firstName} ${formData.lastName}` });
      setIsSubmitted(true);
      toast.success('Demo request submitted!');
    } catch (error) {
      toast.error('Failed to submit. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const interests = ['Patient CRM', 'Lead Management', 'Order Processing', 'Product Catalog', 'Newsletter System', 'SEO Pages', 'Analytics', 'HIPAA', 'DME Supplier Network', 'Clearinghouse & RCM Integrations'];

  const benefits = [
    { icon: Calendar, title: 'Personalized Demo', description: 'Tailored to your business needs.' },
    { icon: Clock, title: '30-Min Session', description: 'Focused demonstration.' },
    { icon: Headphones, title: 'Expert Guidance', description: 'Q&A with specialists.' },
    { icon: Shield, title: 'No Commitment', description: 'Zero pressure.' },
  ];

  return (
    <div className="min-h-screen pt-48">
      {/* Hero */}
      <section className="py-20 px-6 lg:px-8 gradient-animated relative overflow-hidden">
        <div className="absolute inset-0 grid-pattern opacity-50" />
        <div className="absolute top-10 left-20 w-80 h-80 bg-[#014DB7]/10 rounded-full blur-3xl" />
        <div className="absolute bottom-10 right-20 w-96 h-96 bg-[#84CC16]/10 rounded-full blur-3xl" />
        
        <div className="relative max-w-7xl mx-auto text-center stagger-children">
          <div className="badge-lime mx-auto mb-6 animate-bounce-subtle">
            <Rocket className="w-4 h-4" />
            <span>Free Demo</span>
          </div>
          <h1 className="text-display text-[#0A1628] mb-6">
            See DME R'US <span className="gradient-text-mixed">in Action</span>
          </h1>
          <p className="text-subtitle max-w-2xl mx-auto">
            Schedule a personalized demo and discover how our platform transforms DME operations.
          </p>
        </div>
      </section>

      {/* Benefits Bar */}
      <section className="py-12 px-6 lg:px-8 bg-white border-b-2 border-[#014DB7]/10">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8 stagger-grid">
            {benefits.map((benefit, index) => (
              <div key={index} className="flex items-start gap-5 group">
                <div className="w-14 h-14 bg-[#014DB7]/10 rounded-2xl flex items-center justify-center flex-shrink-0 group-hover:bg-[#014DB7] transition-all duration-300 shadow-soft icon-hover">
                  <benefit.icon className="w-6 h-6 text-[#014DB7] group-hover:text-white transition-colors" />
                </div>
                <div>
                  <h3 className="font-bold text-[#0A1628] mb-1">{benefit.title}</h3>
                  <p className="text-sm text-[#64748B]">{benefit.description}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Demo Form */}
      <section className="py-24 px-6 lg:px-8 bg-[#F8FAFC] relative">
        <div className="absolute inset-0 dot-pattern opacity-30" />
        <div className="relative max-w-4xl mx-auto">
          {isSubmitted ? (
            <div className="bg-white rounded-3xl p-12 text-center shadow-hard border-2 border-[#84CC16]/30 stagger-children">
              <div className="w-28 h-28 bg-[#84CC16]/20 rounded-full flex items-center justify-center mx-auto mb-8 animate-scale-in shadow-glow-lime">
                <CheckCircle2 className="w-14 h-14 text-[#84CC16]" />
              </div>
              <h2 className="text-4xl font-bold text-[#0A1628] mb-4">Request Received!</h2>
              <p className="text-xl text-[#64748B] mb-10 max-w-lg mx-auto">
                Our team will reach out within 24 hours to schedule your personalized demo.
              </p>
              <div className="bg-[#F8FAFC] rounded-2xl p-8 max-w-md mx-auto border-2 border-[#014DB7]/10">
                <h3 className="font-bold text-[#0A1628] mb-5">What's Next?</h3>
                <ul className="text-left space-y-4 text-[#64748B]">
                  {['Team reviews your requirements', 'We schedule your demo', 'Personalized platform walkthrough'].map((step, i) => (
                    <li key={i} className="flex items-center gap-4">
                      <span className="w-8 h-8 bg-[#014DB7] rounded-full flex items-center justify-center text-white text-sm font-bold">{i + 1}</span>
                      <span>{step}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          ) : (
            <div className="bg-white rounded-3xl p-8 lg:p-12 shadow-hard border-2 border-[#014DB7]/10">
              <div className="text-center mb-12">
                <h2 className="text-3xl font-bold text-[#0A1628] mb-3">Request Your Demo</h2>
                <p className="text-[#64748B]">Fill out the form and we'll be in touch within 24 hours.</p>
              </div>

              <form onSubmit={handleSubmit} className="space-y-10" data-testid="demo-form">
                {/* Personal Info */}
                <div>
                  <h3 className="text-lg font-bold text-[#0A1628] mb-5 flex items-center gap-3">
                    <div className="w-8 h-8 bg-[#014DB7] rounded-lg flex items-center justify-center">
                      <Users className="w-4 h-4 text-white" />
                    </div>
                    Personal Information
                  </h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div className="space-y-2">
                      <Label className="text-[#0A1628] font-semibold">First Name *</Label>
                      <Input name="firstName" value={formData.firstName} onChange={handleChange} required placeholder="John" data-testid="demo-firstname-input" className="rounded-xl border-2 border-[#014DB7]/10 focus:border-[#014DB7] h-12" />
                    </div>
                    <div className="space-y-2">
                      <Label className="text-[#0A1628] font-semibold">Last Name *</Label>
                      <Input name="lastName" value={formData.lastName} onChange={handleChange} required placeholder="Doe" data-testid="demo-lastname-input" className="rounded-xl border-2 border-[#014DB7]/10 focus:border-[#014DB7] h-12" />
                    </div>
                    <div className="space-y-2">
                      <Label className="text-[#0A1628] font-semibold">Work Email *</Label>
                      <Input name="email" type="email" value={formData.email} onChange={handleChange} required placeholder="john@company.com" data-testid="demo-email-input" className="rounded-xl border-2 border-[#014DB7]/10 focus:border-[#014DB7] h-12" />
                    </div>
                    <div className="space-y-2">
                      <Label className="text-[#0A1628] font-semibold">Phone *</Label>
                      <Input name="phone" type="tel" value={formData.phone} onChange={handleChange} required placeholder="(555) 123-4567" data-testid="demo-phone-input" className="rounded-xl border-2 border-[#014DB7]/10 focus:border-[#014DB7] h-12" />
                    </div>
                  </div>
                </div>

                {/* Company Info */}
                <div>
                  <h3 className="text-lg font-bold text-[#0A1628] mb-5 flex items-center gap-3">
                    <div className="w-8 h-8 bg-[#84CC16] rounded-lg flex items-center justify-center">
                      <Building2 className="w-4 h-4 text-white" />
                    </div>
                    Company Information
                  </h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div className="space-y-2">
                      <Label className="text-[#0A1628] font-semibold">Company Name *</Label>
                      <Input name="company" value={formData.company} onChange={handleChange} required placeholder="Acme DME" data-testid="demo-company-input" className="rounded-xl border-2 border-[#014DB7]/10 focus:border-[#014DB7] h-12" />
                    </div>
                    <div className="space-y-2">
                      <Label className="text-[#0A1628] font-semibold">Job Title *</Label>
                      <Input name="jobTitle" value={formData.jobTitle} onChange={handleChange} required placeholder="Operations Manager" data-testid="demo-jobtitle-input" className="rounded-xl border-2 border-[#014DB7]/10 focus:border-[#014DB7] h-12" />
                    </div>
                    <div className="space-y-2">
                      <Label className="text-[#0A1628] font-semibold">Company Size</Label>
                      <Select value={formData.companySize} onValueChange={(v) => handleSelectChange('companySize', v)}>
                        <SelectTrigger className="rounded-xl border-2 border-[#014DB7]/10 focus:border-[#014DB7] h-12" data-testid="demo-companysize-select">
                          <SelectValue placeholder="Select size" />
                        </SelectTrigger>
                        <SelectContent>
                          {['1-10', '11-50', '51-200', '201-500', '500+'].map(size => (
                            <SelectItem key={size} value={size}>{size} employees</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-2">
                      <Label className="text-[#0A1628] font-semibold">Current Solution</Label>
                      <Select value={formData.currentSolution} onValueChange={(v) => handleSelectChange('currentSolution', v)}>
                        <SelectTrigger className="rounded-xl border-2 border-[#014DB7]/10 focus:border-[#014DB7] h-12" data-testid="demo-currentsolution-select">
                          <SelectValue placeholder="What are you using?" />
                        </SelectTrigger>
                        <SelectContent>
                          {[['spreadsheets', 'Spreadsheets/Excel'], ['paper', 'Paper-based'], ['other-crm', 'Other CRM'], ['custom', 'Custom Software'], ['none', 'None']].map(([val, label]) => (
                            <SelectItem key={val} value={val}>{label}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    
                    {/* States Licensed to Sell */}
                    <div className="space-y-2" ref={statesRef}>
                      <Label className="text-[#0A1628] font-semibold flex items-center gap-2">
                        <MapPin className="w-4 h-4 text-[#014DB7]" />
                        States Licensed to Sell
                      </Label>
                      <div className="relative">
                        <div 
                          onClick={() => setStatesDropdownOpen(!statesDropdownOpen)}
                          className="rounded-xl border-2 border-[#014DB7]/10 focus:border-[#014DB7] min-h-12 p-3 cursor-pointer flex items-center justify-between bg-white hover:border-[#014DB7]/30 transition-colors"
                          data-testid="demo-states-select"
                        >
                          <div className="flex flex-wrap gap-1 flex-1">
                            {formData.licensedStates.length === 0 ? (
                              <span className="text-gray-400">Select states...</span>
                            ) : (
                              formData.licensedStates.slice(0, 3).map((state) => (
                                <span key={state} className="bg-[#014DB7]/10 text-[#014DB7] px-2 py-1 rounded-lg text-xs font-medium">
                                  {state}
                                </span>
                              ))
                            )}
                            {formData.licensedStates.length > 3 && (
                              <span className="bg-[#84CC16]/20 text-[#84CC16] px-2 py-1 rounded-lg text-xs font-bold">
                                +{formData.licensedStates.length - 3} more
                              </span>
                            )}
                          </div>
                          <ChevronDown className={`w-5 h-5 text-gray-400 transition-transform ${statesDropdownOpen ? 'rotate-180' : ''}`} />
                        </div>
                        {statesDropdownOpen && (
                          <div className="absolute z-50 w-full mt-1 bg-white border-2 border-[#014DB7]/20 rounded-xl shadow-lg max-h-60 overflow-y-auto">
                            <div className="p-2 border-b border-gray-100 flex justify-between items-center">
                              <span className="text-xs text-gray-500">{formData.licensedStates.length} selected</span>
                              {formData.licensedStates.length > 0 && (
                                <button 
                                  type="button"
                                  onClick={(e) => { e.stopPropagation(); setFormData(prev => ({...prev, licensedStates: []})); }}
                                  className="text-xs text-red-500 hover:text-red-700"
                                >
                                  Clear all
                                </button>
                              )}
                            </div>
                            {US_STATES.map((state) => {
                              const isSelected = formData.licensedStates.includes(state);
                              return (
                                <div
                                  key={state}
                                  onClick={(e) => { e.stopPropagation(); handleMultiSelectChange('licensedStates', state); }}
                                  className={`px-3 py-2 cursor-pointer flex items-center gap-2 hover:bg-[#014DB7]/5 ${isSelected ? 'bg-[#014DB7]/10' : ''}`}
                                >
                                  <div className={`w-4 h-4 rounded border-2 flex items-center justify-center ${isSelected ? 'bg-[#014DB7] border-[#014DB7]' : 'border-gray-300'}`}>
                                    {isSelected && <CheckCircle2 className="w-3 h-3 text-white" />}
                                  </div>
                                  <span className="text-sm">{state}</span>
                                </div>
                              );
                            })}
                          </div>
                        )}
                      </div>
                    </div>

                    {/* DME Categories */}
                    <div className="space-y-2" ref={categoriesRef}>
                      <Label className="text-[#0A1628] font-semibold flex items-center gap-2">
                        <Package className="w-4 h-4 text-[#84CC16]" />
                        DME Categories You Sell
                      </Label>
                      <div className="relative">
                        <div 
                          onClick={() => setCategoriesDropdownOpen(!categoriesDropdownOpen)}
                          className="rounded-xl border-2 border-[#014DB7]/10 focus:border-[#014DB7] min-h-12 p-3 cursor-pointer flex items-center justify-between bg-white hover:border-[#014DB7]/30 transition-colors"
                          data-testid="demo-categories-select"
                        >
                          <div className="flex flex-wrap gap-1 flex-1">
                            {formData.dmeCategories.length === 0 ? (
                              <span className="text-gray-400">Select categories...</span>
                            ) : (
                              formData.dmeCategories.slice(0, 2).map((cat) => (
                                <span key={cat} className="bg-[#84CC16]/10 text-[#65a30d] px-2 py-1 rounded-lg text-xs font-medium">
                                  {cat}
                                </span>
                              ))
                            )}
                            {formData.dmeCategories.length > 2 && (
                              <span className="bg-[#014DB7]/20 text-[#014DB7] px-2 py-1 rounded-lg text-xs font-bold">
                                +{formData.dmeCategories.length - 2} more
                              </span>
                            )}
                          </div>
                          <ChevronDown className={`w-5 h-5 text-gray-400 transition-transform ${categoriesDropdownOpen ? 'rotate-180' : ''}`} />
                        </div>
                        {categoriesDropdownOpen && (
                          <div className="absolute z-50 w-full mt-1 bg-white border-2 border-[#84CC16]/20 rounded-xl shadow-lg max-h-60 overflow-y-auto">
                            <div className="p-2 border-b border-gray-100 flex justify-between items-center">
                              <span className="text-xs text-gray-500">{formData.dmeCategories.length} selected</span>
                              {formData.dmeCategories.length > 0 && (
                                <button 
                                  type="button"
                                  onClick={(e) => { e.stopPropagation(); setFormData(prev => ({...prev, dmeCategories: []})); }}
                                  className="text-xs text-red-500 hover:text-red-700"
                                >
                                  Clear all
                                </button>
                              )}
                            </div>
                            {DME_CATEGORIES.map((category) => {
                              const isSelected = formData.dmeCategories.includes(category);
                              return (
                                <div
                                  key={category}
                                  onClick={(e) => { e.stopPropagation(); handleMultiSelectChange('dmeCategories', category); }}
                                  className={`px-3 py-2 cursor-pointer flex items-center gap-2 hover:bg-[#84CC16]/5 ${isSelected ? 'bg-[#84CC16]/10' : ''}`}
                                >
                                  <div className={`w-4 h-4 rounded border-2 flex items-center justify-center ${isSelected ? 'bg-[#84CC16] border-[#84CC16]' : 'border-gray-300'}`}>
                                    {isSelected && <CheckCircle2 className="w-3 h-3 text-white" />}
                                  </div>
                                  <span className="text-sm">{category}</span>
                                </div>
                              );
                            })}
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </div>

                {/* Interests */}
                <div>
                  <h3 className="text-lg font-bold text-[#0A1628] mb-5">Features You're Interested In</h3>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                    {interests.map((interest) => {
                      const isSelected = formData.interests.includes(interest);
                      return (
                        <div 
                          key={interest} 
                          onClick={() => handleInterestChange(interest)}
                          data-testid={`demo-interest-${interest.toLowerCase().replace(/\s+/g, '-')}`}
                          className={`flex items-center gap-3 p-4 rounded-xl border-2 transition-all cursor-pointer ${
                            isSelected 
                              ? 'border-[#84CC16] bg-[#84CC16]/10' 
                              : 'border-[#014DB7]/10 hover:border-[#014DB7]/30'
                          }`}
                        >
                          <div className={`w-4 h-4 rounded border-2 flex items-center justify-center transition-all ${
                            isSelected 
                              ? 'bg-[#84CC16] border-[#84CC16]' 
                              : 'border-gray-300 bg-white'
                          }`}>
                            {isSelected && (
                              <CheckCircle2 className="w-3 h-3 text-white" />
                            )}
                          </div>
                          <Label className="text-sm cursor-pointer font-medium text-[#0A1628]">{interest}</Label>
                        </div>
                      );
                    })}
                  </div>
                </div>

                {/* Message */}
                <div className="space-y-2">
                  <Label className="text-[#0A1628] font-semibold">Additional Info</Label>
                  <Textarea name="message" value={formData.message} onChange={handleChange} rows={4} placeholder="Tell us about your needs..." data-testid="demo-message-input" className="resize-none rounded-xl border-2 border-[#014DB7]/10 focus:border-[#014DB7]" />
                </div>

                {/* Newsletter */}
                <div className="flex items-center gap-3 p-5 bg-[#84CC16]/10 rounded-xl border-2 border-[#84CC16]/20">
                  <Checkbox id="newsletter" checked={formData.subscribeNewsletter} onCheckedChange={(c) => setFormData((prev) => ({ ...prev, subscribeNewsletter: c }))} data-testid="demo-newsletter-checkbox" />
                  <Label htmlFor="newsletter" className="text-sm cursor-pointer text-[#0A1628]">Subscribe to DME industry insights</Label>
                </div>

                {/* Submit */}
                <Button type="submit" disabled={isSubmitting} data-testid="demo-submit-btn" className="w-full btn-lift bg-[#014DB7] text-white hover:bg-[#003A8C] py-6 text-lg font-bold rounded-xl shadow-glow-blue">
                  {isSubmitting ? 'Submitting...' : 'Request Demo'}
                </Button>

                <p className="text-center text-sm text-[#64748B]">
                  By submitting, you agree to our Privacy Policy and Terms of Service.
                </p>
              </form>
            </div>
          )}
        </div>
      </section>
    </div>
  );
};

export default DemoPage;
